import * as qs from 'qs'
import _ from 'lodash'
import type Passport from '@patsnap-utils/passport/src/passport'
import { CommonApis } from './apis/common.api'
import type { HeaderService } from '@patsnap-utils/biz'
import { NeedAddTrialActionType, type IAddTrialExtendBody } from './apis/common.api.types'
import { isAxiosError } from './exception'
import { Site, SLD } from '@patsnap-utils/biz';
import { addTrialDeferred } from '@eureka/utils/account'
import { initCookieService } from '@eureka/utils/init-cookie'
import { getSearchParams } from '@eureka/utils/url'

const MAX_TRIAL_STORAGE_KEY = 'ADDTRIAL_clickcount'
const MAX_TRIAL_STORAGE_COUNT = 5
const NO_PERMISSION_ADD_TRIAL_ERROR_CODE = 90112055

const isNoPermissionPage = () => typeof window !== 'undefined' && /^\/403\/?$/.test(window.location.pathname);

const redirectToNoPermissionPage = () => {
    if (typeof window === 'undefined') return;
    if (isNoPermissionPage()) return;
    window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href;
}

const redirectToAccountStatusPage = () => {
    if (typeof window === 'undefined') return;
    const pathName = new Site({ sld: SLD.EUREKA, pathname: 'account-status/' }).href;
    window.location.href = `${pathName}?account_status_from=${encodeURIComponent(window.location.href)}`;
}

const getNeedAddTrialErrorCode = (data: unknown) => _.get(data, 'error_code') ??
    _.get(data, 'data.error_code') ??
    _.get(data, 'response.data.error_code') ??
    _.get(data, 'cause.error_code');

const isNoPermissionAddTrialError = (data: unknown) => getNeedAddTrialErrorCode(data) === NO_PERMISSION_ADD_TRIAL_ERROR_CODE;

class AddTrialService {
    passport: Passport | void = undefined

    ENABLE_AUTO_ADD_TRIAL = true;
    async addTrial(headerService: HeaderService, passport: Passport | void) {
        try {
            this.passport = passport
            const grantedProductIds: string[] | undefined = headerService.account?._info.granted_product_ids
            if (grantedProductIds === undefined) {
                throw new Error('Granted_product_ids is undefined!')
            }
            const extendBody = this.getAddTrialExtendBody()
            const empowerType = this.getEmpowerType(extendBody.from || (extendBody as any).start_from);

            const needAddTrialData = await CommonApis.needAddTrial(empowerType || '', extendBody.from, extendBody);
            if (isNoPermissionAddTrialError(needAddTrialData)) {
                redirectToNoPermissionPage();
                return;
            }

            // 新版接口返回 { actionType } 对象，根据 actionType 决策
            const actionType = _.get(needAddTrialData, 'data.action_type', '') as NeedAddTrialActionType | string;
            this.ENABLE_AUTO_ADD_TRIAL = false;

            switch (actionType) {
            case NeedAddTrialActionType.DIRECT_LOGIN:
                // 当前用户已有权限，直接进入产品，无需任何操作
                break;
            case NeedAddTrialActionType.GRANT_INDIVIDUAL_TENANT_PRIVILEGE: {
                // 后端已在本次请求中完成赋权，刷新页面使权限生效
                // 保护：防止因异常导致无限刷新
                const trialCounts = Number(sessionStorage.getItem(MAX_TRIAL_STORAGE_KEY) || 0)
                if (trialCounts >= MAX_TRIAL_STORAGE_COUNT) {
                    sessionStorage.removeItem(MAX_TRIAL_STORAGE_KEY)
                    window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href
                    throw new Error(`Trial counts is over ${MAX_TRIAL_STORAGE_COUNT}!`);
                }
                sessionStorage.setItem(MAX_TRIAL_STORAGE_KEY, String(trialCounts + 1))
                localStorage.removeItem('patsnap_header_account_info')
                console.warn('[AddTrial]', '赋权成功，刷新页面生效')
                this.passport?.removeLocal()
                window.location.reload()
                break;
            }
            case NeedAddTrialActionType.CONTACT_ADMIN:
                // 企业不允许创建个人租户，跳转 account-status 页面
                redirectToAccountStatusPage();
                break;
            // switch_to_individual_tenant / switch_and_grant_privilege / create_individual_tenant：
            // 需要切换/创建个人租户，属于交互流程，由上层（如 package-status-dialog）统一处理
            default:
                // 其他情况，跳转 account-status 页面
                redirectToAccountStatusPage();
                break;
            }
        } catch (error) {
            if (isNoPermissionAddTrialError(error)) {
                redirectToNoPermissionPage();
                return;
            }
            console.error('[AddTrial]', error)
        } finally {
            this.ENABLE_AUTO_ADD_TRIAL = false
            addTrialDeferred.resolve(true);
        }
    }

    isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        );
    }

    getSource() {
        const searchParams = getSearchParams();
        const from = searchParams.get('from') || searchParams.get('start_from');
        if (from && (from.startsWith('eureka-seo-') || from.startsWith('website-seo-'))) {
            return from
        }
        return initCookieService.getCampaignName();
    }

    getAddTrialExtendBody(): IAddTrialExtendBody {
        const hash = window.location.hash;
        const queryString = hash.includes('?') ? '?' + hash.split('?')[1] : window.location.search;

        const query = qs.parse(queryString, {
            ignoreQueryPrefix: true,
        })

        const supplyPermissionParams = initCookieService.getSupplyPermissionParams();

        // warning: 直接全部给后端可能会带有多余的参数
        // @ts-ignore
        return {
            from: this.getSource(),
            ...query,
            media_type: this.isMobileDevice() ? 'Mobile' : '',
            ...supplyPermissionParams,
        }
    }

    getEmpowerType(from?: string) {
        const fromLifeScienceCheckList = ['synapse', 'chemical', 'bio', 'hiro_ls', 'ls']
        if (fromLifeScienceCheckList.includes(from || '')) return 'LS';
        if (from === 'ls_plg') return 'LS_PLG';
        if (from === 'discovery_seo_company') return 'INSIGHTS';
        if (from?.includes('eureka-seo-material')) return 'MATERIAL';
        if (from === 'feishu') return 'FEISHU';
        return 'LS_PLG';
    }
}

export const addTrialService = new AddTrialService()

export function errorHandleIgnore403(e: Error) {
    return isAxiosError(e) && e.response?.status === 403;
}
